import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, X, GripVertical, Lightbulb, Loader2, MoreVertical, Shuffle, Filter, Scissors, Trash2, Scissors as TrimIcon, ArrowDown, ArrowUp } from 'lucide-react';
import { CopyButton } from '../fileProcessing/FileCardBase';
import {
  normalizeKeyword,
  sanitizeKeyword,
  shuffleKeywords,
  removePlurals,
  splitToSingleWords,
  removeMultiWordKeywords,
  trimKeywords,
  parseKeywordsInput
} from '../../utils/autoFix';

interface SimpleKeywordsManagerProps {
  keywords: string[];
  fileId: string;
  onSave: (fileId: string, metadata: { keywords: string[] }) => Promise<void>;
  disabled?: boolean;
  maxKeywords?: number;
  maxKeywordLength?: number;
}

interface DragState {
  isDragging: boolean;
  draggedIndex: number | null;
  dragOverIndex: number | null;
}

interface SynonymState {
  keywordIndex: number | null;
  keyword: string;
  synonyms: string[];
  selectedSynonyms: Set<string>;
  loading: boolean;
  error: string | null;
}

export const SimpleKeywordsManager: React.FC<SimpleKeywordsManagerProps> = ({
  keywords,
  fileId,
  onSave,
  disabled = false,
  maxKeywords = 100,
  maxKeywordLength = 80
}) => {
  const [localKeywords, setLocalKeywords] = useState<string[]>(keywords);
  const [newKeyword, setNewKeyword] = useState('');
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedIndex: null,
    dragOverIndex: null
  });
  const [synonymState, setSynonymState] = useState<SynonymState>({
    keywordIndex: null,
    keyword: '',
    synonyms: [],
    selectedSynonyms: new Set(),
    loading: false,
    error: null
  });
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [showTrimModal, setShowTrimModal] = useState(false);
  const [trimValue, setTrimValue] = useState('');
  const [insertAtBeginning, setInsertAtBeginning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const synonymPopoverRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update local keywords when prop changes
  useEffect(() => {
    setLocalKeywords(keywords);
  }, [keywords]);

  // Handle click outside synonym popover and dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (synonymPopoverRef.current && !synonymPopoverRef.current.contains(event.target as Node)) {
        setSynonymState(prev => ({ ...prev, keywordIndex: null }));
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAdvancedActions(false);
      }
    };

    if (synonymState.keywordIndex !== null || showAdvancedActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [synonymState.keywordIndex, showAdvancedActions]);

  // Save keywords when changed
  const saveKeywords = useCallback(async (newKeywords: string[]) => {
    if (JSON.stringify(newKeywords) !== JSON.stringify(keywords)) {
      await onSave(fileId, { keywords: newKeywords });
    }
  }, [fileId, onSave, keywords]);

  // Fetch synonyms from our Research Engine
  const fetchSynonyms = useCallback(async (word: string): Promise<string[]> => {
    try {
      const response = await fetch(`https://research.metapic.app/v1/synonym?word=${encodeURIComponent(word)}`, {
        headers: {
          'x-api-key': import.meta.env.VITE_RESEARCH_ENGINE_API_KEY || ''
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Check if synonyms were found
      if (!data.found || !data.synonyms || data.synonyms.length === 0) {
        return [];
      }

      const synonyms = data.synonyms;

      // Clean, filter and limit synonyms
      const cleanedSynonyms = synonyms
        .map((syn: string) => sanitizeKeyword(syn))
        .filter((syn: string) => {
          return syn.length > 1 &&
                 syn.length <= maxKeywordLength &&
                 !localKeywords.some(existing => normalizeKeyword(existing) === normalizeKeyword(syn));
        })
        .slice(0, 8); // Limit to 8 synonyms for clean UI

      return cleanedSynonyms;
    } catch (error) {
      throw error;
    }
  }, [localKeywords, maxKeywordLength]);

  // Handle lightbulb click to show synonyms
  const handleSynonymClick = useCallback(async (index: number, keyword: string) => {
    // Close if clicking same keyword
    if (synonymState.keywordIndex === index) {
      setSynonymState(prev => ({ ...prev, keywordIndex: null }));
      return;
    }

    // Set loading state
    setSynonymState({
      keywordIndex: index,
      keyword,
      synonyms: [],
      selectedSynonyms: new Set(),
      loading: true,
      error: null
    });

    try {
      const synonyms = await fetchSynonyms(keyword);
      setSynonymState(prev => ({
        ...prev,
        synonyms,
        loading: false
      }));
    } catch (error) {
      setSynonymState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load synonyms'
      }));
    }
  }, [synonymState.keywordIndex, fetchSynonyms]);

  // Handle synonym selection
  const handleSynonymSelect = useCallback((synonym: string) => {
    setSynonymState(prev => {
      const newSelected = new Set(prev.selectedSynonyms);
      if (newSelected.has(synonym)) {
        newSelected.delete(synonym);
      } else {
        newSelected.add(synonym);
      }
      return { ...prev, selectedSynonyms: newSelected };
    });
  }, []);

  // Add selected synonyms to keywords
  const handleAddSelectedSynonyms = useCallback(async () => {
    const selectedArray = Array.from(synonymState.selectedSynonyms);
    if (selectedArray.length === 0) return;

    const updatedKeywords = [...localKeywords, ...selectedArray];

    // Enforce limit
    const finalKeywords = updatedKeywords.length > maxKeywords
      ? updatedKeywords.slice(0, maxKeywords)
      : updatedKeywords;

    setLocalKeywords(finalKeywords);
    await saveKeywords(finalKeywords);

    // Close popover
    setSynonymState(prev => ({ ...prev, keywordIndex: null }));
  }, [synonymState.selectedSynonyms, localKeywords, maxKeywords, saveKeywords]);

  // Add all synonyms
  const handleAddAllSynonyms = useCallback(async () => {
    if (synonymState.synonyms.length === 0) return;

    const updatedKeywords = [...localKeywords, ...synonymState.synonyms];

    // Enforce limit
    const finalKeywords = updatedKeywords.length > maxKeywords
      ? updatedKeywords.slice(0, maxKeywords)
      : updatedKeywords;

    setLocalKeywords(finalKeywords);
    await saveKeywords(finalKeywords);

    // Close popover
    setSynonymState(prev => ({ ...prev, keywordIndex: null }));
  }, [synonymState.synonyms, localKeywords, maxKeywords, saveKeywords]);

  // Advanced actions
  const handleRandomize = useCallback(async () => {
    const randomized = shuffleKeywords(localKeywords);
    setLocalKeywords(randomized);
    await saveKeywords(randomized);
    setShowAdvancedActions(false);
  }, [localKeywords, saveKeywords]);

  const handleRemovePlurals = useCallback(async () => {
    const filtered = removePlurals(localKeywords);
    setLocalKeywords(filtered);
    await saveKeywords(filtered);
    setShowAdvancedActions(false);
  }, [localKeywords, saveKeywords]);

  const handleOnlyOneWord = useCallback(async () => {
    const singleWords = splitToSingleWords(localKeywords);
    setLocalKeywords(singleWords);
    await saveKeywords(singleWords);
    setShowAdvancedActions(false);
  }, [localKeywords, saveKeywords]);

  const handleRemoveMultiWord = useCallback(async () => {
    const singleWordsOnly = removeMultiWordKeywords(localKeywords);
    setLocalKeywords(singleWordsOnly);
    await saveKeywords(singleWordsOnly);
    setShowAdvancedActions(false);
  }, [localKeywords, saveKeywords]);

  const handleTrimKeywords = useCallback(async () => {
    const numToKeep = parseInt(trimValue);
    if (isNaN(numToKeep) || numToKeep < 1 || numToKeep >= localKeywords.length) {
      setTrimValue('');
      setShowTrimModal(false);
      return;
    }

    const trimmed = trimKeywords(localKeywords, numToKeep);
    setLocalKeywords(trimmed);
    await saveKeywords(trimmed);
    setTrimValue('');
    setShowTrimModal(false);
    setShowAdvancedActions(false);
  }, [trimValue, localKeywords, saveKeywords]);

  // Add keywords with bulk support
  const handleAddKeywords = useCallback(async () => {
    if (!newKeyword.trim()) return;

    const newKeywords = parseKeywordsInput(newKeyword);
    if (newKeywords.length === 0) return;

    let updatedKeywords;
    if (insertAtBeginning) {
      updatedKeywords = [...newKeywords, ...localKeywords];
    } else {
      updatedKeywords = [...localKeywords, ...newKeywords];
    }

    // Enforce limit
    const finalKeywords = updatedKeywords.length > maxKeywords
      ? updatedKeywords.slice(0, maxKeywords)
      : updatedKeywords;

    setLocalKeywords(finalKeywords);
    await saveKeywords(finalKeywords);
    setNewKeyword('');
    inputRef.current?.focus();
  }, [newKeyword, insertAtBeginning, localKeywords, maxKeywords, saveKeywords]);

  // Handle key press for add keywords
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeywords();
    }
  }, [handleAddKeywords]);


  // Remove keyword
  const removeKeyword = (index: number) => {
    if (disabled) return;
    const newKeywords = localKeywords.filter((_, i) => i !== index);
    setLocalKeywords(newKeywords);
    saveKeywords(newKeywords);
  };


  // Handle drag start
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (disabled) return;
    setDragState({ isDragging: true, draggedIndex: index, dragOverIndex: null });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (disabled) return;
    e.preventDefault();
    setDragState(prev => ({ ...prev, dragOverIndex: index }));
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    if (disabled || dragState.draggedIndex === null) return;
    e.preventDefault();

    const newKeywords = [...localKeywords];
    const draggedItem = newKeywords.splice(dragState.draggedIndex, 1)[0];
    newKeywords.splice(dropIndex, 0, draggedItem);

    setLocalKeywords(newKeywords);
    saveKeywords(newKeywords);
    setDragState({ isDragging: false, draggedIndex: null, dragOverIndex: null });
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDragState({ isDragging: false, draggedIndex: null, dragOverIndex: null });
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">
            Keywords ({localKeywords.length})
          </label>
        </div>
        <div className="flex items-center space-x-3">
          {/* Advanced Actions Dropdown */}
          {localKeywords.length > 0 && (
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setShowAdvancedActions(!showAdvancedActions)}
                disabled={disabled}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <MoreVertical className="w-3 h-3" />
                Advanced
              </button>

              {showAdvancedActions && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px]">
                  <div className="p-1">
                    <button
                      onClick={handleRandomize}
                      disabled={disabled}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Shuffle className="w-3 h-3" />
                      Randomize Order
                    </button>

                    <button
                      onClick={handleRemovePlurals}
                      disabled={disabled}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Filter className="w-3 h-3" />
                      Remove Plurals
                    </button>

                    <button
                      onClick={handleOnlyOneWord}
                      disabled={disabled}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Scissors className="w-3 h-3" />
                      Split to Single Words
                    </button>

                    <button
                      onClick={handleRemoveMultiWord}
                      disabled={disabled}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove Multi-Word
                    </button>

                    <button
                      onClick={() => setShowTrimModal(true)}
                      disabled={disabled || localKeywords.length <= 1}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <TrimIcon className="w-3 h-3" />
                      Trim Keywords
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <span className="text-xs text-gray-500">{localKeywords.length}</span>
          <CopyButton content={localKeywords.join(', ')} />
        </div>
      </div>

      {/* Keywords Display */}
      {localKeywords.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border min-h-[60px]">
          {localKeywords.map((keyword, index) => {
            const isShowingSynonyms = synonymState.keywordIndex === index;
            return (
              <div
                key={`${keyword}-${index}`}
                className="relative"
              >
                <div
                  draggable={!disabled}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center space-x-1 px-2 py-1 bg-white border rounded-md text-sm transition-all group ${
                    dragState.dragOverIndex === index ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                  } ${dragState.draggedIndex === index ? 'opacity-50' : ''} ${
                    disabled ? 'cursor-not-allowed' : 'cursor-move hover:border-gray-400'
                  } ${
                    isShowingSynonyms ? 'ring-2 ring-yellow-400 ring-offset-1' : ''
                  }`}
                >
                  {!disabled && <GripVertical className="w-3 h-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600" />}
                  <span className="select-none">{keyword}</span>
                  {!disabled && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSynonymClick(index, keyword);
                        }}
                        className={`ml-1 hover:text-yellow-600 p-0.5 rounded-full transition-colors text-blue-600 hover:bg-blue-200 ${
                          isShowingSynonyms ? 'text-yellow-600 bg-yellow-100' : ''
                        }`}
                        title="Find dictionary synonyms (not AI-generated)"
                      >
                        <Lightbulb className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeKeyword(index)}
                        className="ml-1 hover:text-red-600 p-0.5 rounded-full transition-colors text-blue-600 hover:bg-blue-200"
                        title="Remove keyword"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>

                {/* Synonym Popover */}
                {isShowingSynonyms && (
                  <div
                    ref={synonymPopoverRef}
                    className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[280px] max-w-[320px]"
                  >
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2 pb-2 border-b">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Synonyms for "{keyword}"
                          </span>
                        </div>
                        <div className="flex items-center gap-1" title="Synonyms are sourced from thesaurus database, not AI-generated">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-xs text-gray-500">Dictionary</span>
                        </div>
                      </div>

                      {synonymState.loading && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="ml-2 text-sm text-gray-500">Loading synonyms...</span>
                        </div>
                      )}

                      {synonymState.error && (
                        <div className="py-4 text-center">
                          <p className="text-sm text-red-600">{synonymState.error}</p>
                        </div>
                      )}

                      {!synonymState.loading && !synonymState.error && (
                        <>
                          {synonymState.synonyms.length > 0 ? (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                {synonymState.synonyms.map((synonym, synIndex) => (
                                  <label
                                    key={synIndex}
                                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={synonymState.selectedSynonyms.has(synonym)}
                                      onChange={() => handleSynonymSelect(synonym)}
                                      className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">{synonym}</span>
                                  </label>
                                ))}
                              </div>

                              <div className="flex gap-2 pt-2 border-t">
                                <button
                                  onClick={handleAddSelectedSynonyms}
                                  disabled={synonymState.selectedSynonyms.size === 0}
                                  className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Add ({synonymState.selectedSynonyms.size})
                                </button>
                                <button
                                  onClick={handleAddAllSynonyms}
                                  className="flex-1 px-3 py-1.5 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition-colors"
                                >
                                  Add All
                                </button>
                                <button
                                  onClick={() => setSynonymState(prev => ({ ...prev, keywordIndex: null }))}
                                  className="px-2 py-1.5 text-gray-500 hover:text-gray-700 transition-colors"
                                  title="Close"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>

                              {/* Info Footer */}
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-400 text-center">
                                  💡 Synonyms from dictionary database
                                </p>
                              </div>
                            </>
                          ) : (
                            <div className="py-4 text-center">
                              <div className="mb-2">
                                <span className="text-2xl">📚</span>
                              </div>
                              <p className="text-sm text-gray-600 font-medium mb-1">No synonyms found for "{keyword}"</p>
                              <p className="text-xs text-gray-500 leading-relaxed">
                                Dictionary synonyms aren't available for this word.<br/>
                                Try related words or check spelling.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Keywords Input */}
      {!disabled && (
        <div className="space-y-1">
          <div className="flex space-x-2">
            <input
              ref={inputRef}
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add keywords: photography, wedding, portrait, professional..."
              maxLength={maxKeywordLength * 10} // Allow longer input for bulk
              className="flex-1 px-3 py-2.5 border rounded-lg text-sm focus:outline-none transition-colors border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 hover:border-gray-400"
              disabled={localKeywords.length >= maxKeywords}
            />

            {/* Keyword Insertion Order Toggle */}
            <button
              onClick={() => setInsertAtBeginning(!insertAtBeginning)}
              disabled={disabled}
              className={`px-3 py-2.5 border rounded-lg transition-colors flex items-center ${
                disabled
                  ? 'bg-gray-100 border-gray-200 cursor-not-allowed text-gray-400'
                  : insertAtBeginning
                    ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                    : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
              title={insertAtBeginning ? 'Add to beginning (first order)' : 'Add to end (last order)'}
            >
              {insertAtBeginning ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
            </button>

            <button
              onClick={handleAddKeywords}
              disabled={!newKeyword.trim() || disabled || localKeywords.length >= maxKeywords}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              title={localKeywords.length >= maxKeywords ? 'Maximum keywords reached' : 'Add keywords (Enter)'}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Live feedback for new keywords */}
          {newKeyword.trim() && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {insertAtBeginning ? (
                  <span className="flex items-center">
                    <ArrowUp className="w-3 h-3 mr-1" />
                    Adding to beginning
                  </span>
                ) : (
                  <span className="flex items-center">
                    <ArrowDown className="w-3 h-3 mr-1" />
                    Adding to end
                  </span>
                )}
              </span>
              <span className="text-xs text-blue-600">
                +{parseKeywordsInput(newKeyword).length} keywords
              </span>
            </div>
          )}

          {/* Warnings */}
          {localKeywords.length >= maxKeywords * 0.9 && (
            <p className="text-xs text-orange-600">
              ⚠️ Approaching limit: {localKeywords.length} keywords
            </p>
          )}
        </div>
      )}

      {/* Trim Keywords Modal */}
      {showTrimModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Trim Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              Current keywords: <span className="font-medium">{localKeywords.length}</span>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Enter the number of keywords you want to keep. The excess keywords will be removed from the end.
            </p>
            <div className="mb-4">
              <input
                type="number"
                value={trimValue}
                onChange={(e) => setTrimValue(e.target.value)}
                placeholder={`1-${localKeywords.length - 1}`}
                min="1"
                max={localKeywords.length - 1}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleTrimKeywords}
                disabled={!trimValue.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Trim Keywords
              </button>
              <button
                onClick={() => {
                  setShowTrimModal(false);
                  setTrimValue('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleKeywordsManager;
